import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import type { ApiFailure, ApiResult } from '@/api/client'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { windowOptions } from './windows'

export interface DiscoveryRulesValue {
  max_age_hours: number
  exclude_phrases: string[]
}

/**
 * What counts as a lead: how old a post may be, and the phrases that mean it
 * is not one. One form for the Settings panel and the wizard step.
 *
 * Settings consolidation (2026-09-15): was `FilterCard` on the packs screen.
 * `productName` is a prop rather than `useBrand()` so the form renders without
 * a provider — the help copy is the only thing that needs the name.
 */
export function DiscoveryRulesForm({
  value,
  customised,
  productName,
  onSubmit,
  submitLabel,
  disabled = false,
}: {
  value: DiscoveryRulesValue
  /** Whether the exclusion list is the tenant's own yet, or still the defaults. */
  customised: boolean
  productName: string
  onSubmit: (value: DiscoveryRulesValue) => Promise<ApiResult<unknown>>
  submitLabel: string
  disabled?: boolean
}): JSX.Element {
  const [hours, setHours] = useState(value.max_age_hours)
  const [phrases, setPhrases] = useState(value.exclude_phrases.join(', '))
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    const result = await onSubmit({
      max_age_hours: hours,
      exclude_phrases: phrases
        .split(',')
        .map((phrase) => phrase.trim())
        .filter(Boolean),
    })
    setBusy(false)
    if (!result.ok) setFailure(result.failure)
  }

  return (
    <form
      className="ar-stack"
      aria-label="Discovery rules"
      onSubmit={(event) => void submit(event)}
    >
      <Field
        label="Only posts newer than"
        help="A post we cannot date is dropped, never kept. Answering a three-month-old request reads worse than not answering it."
      >
        {(aria) => (
          <select
            className="ar-select"
            {...aria}
            value={hours}
            disabled={disabled}
            onChange={(event) => {
              setHours(Number(event.target.value))
              setFailure(null)
            }}
          >
            {windowOptions(value.max_age_hours).map((option) => (
              <option key={option.hours} value={option.hours}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field
        label="Skip posts containing"
        help={
          customised
            ? 'Recruitment posts match every one of these keywords and are never a lead.'
            : `${productName}'s defaults, shown so you can see what is filtering. Editing them makes the list yours.`
        }
      >
        {(aria) => (
          <textarea
            className="ar-textarea"
            {...aria}
            rows={4}
            value={phrases}
            disabled={disabled}
            onChange={(event) => {
              setPhrases(event.target.value)
              setFailure(null)
            }}
          />
        )}
      </Field>

      <InlineError failure={failure} />

      <div className="ar-row">
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          Posts whose age we cannot read are always dropped.
        </span>
        <span className="ar-card__spacer" />
        <Button type="submit" tone="primary" disabled={disabled} busy={busy} busyLabel="Saving">
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
